import { TypeaheadSection } from '@/Typeahead/TypeaheadSection';
import { mergeClassNames } from '@/utils/mergeClassNames';
import { useResizeObserver } from '@react-aria/utils';
import { Icon, IconName, Spinner } from '@zapier/design-system';
import { clsx } from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import styles from './Typeahead.module.scss';

import { Description } from '@/Description/Description';
import { ErrorMessage } from '@/ErrorMessage/ErrorMessage';
import { Label } from '@/Label/Label';
import { forwardRef, useMemo } from 'react';
import { useFilter } from 'react-aria';
import type { Key } from 'react-aria-components';
import {
  ComboBox,
  Input,
  ListBox,
  Popover,
  Provider,
  ComboBoxProps as ReactAriaComboBoxProps,
} from 'react-aria-components';
import { TypeaheadContext } from './TypeaheadContext';
import { TypeaheadItem } from './TypeaheadItem';
import { TypeaheadRightSlot } from './TypeaheadRightSlot';

// TODO: We should really use a generic type here that extends object, but it's
// nearly impossible to use type parameterized props along with React's
// forwardRef. I don't like any of these solutions.
//
// https://fettblog.eu/typescript-react-generic-forward-refs/
//
// We can remove forwardRef once we require React 19...
//
// https://react.dev/blog/2024/12/05/react-19#ref-as-a-prop
//
type Item = {
  id: Key;
  name: string;
};

export type TypeaheadRootProps = Omit<
  ReactAriaComboBoxProps<Item>,
  // Omits children because the children prop is rendered directly inside of the RAC `ListBox`, not the RAC `ComboBox`.
  'children'
> & {
  /** The children of the component. For static items, render `Typeahead.Item`s.
   * For dynamic items, provide a function, which receives `item` as an
   * argument. */
  children: React.ReactNode | ((item: Item) => React.ReactNode);

  /** Optional property for overriding styles with CSS-in-JS or defining a
   * custom class on the Typeahead PopOver element. Use with caution as the DOM
   * structure of the component may change. */
  classNamePopOver?: string;

  /** Message to display when there are no matches found. */
  emptyStateMessage?: string;

  /** Message to display when there is a validation error. `isErrored` must be
   * `true` in order for the `errorMessage` to be displayed. */
  errorMessage?: string;

  /** Description to display below the input. */
  helpText?: string;

  /** Customize the left icon by changing the name or passing in `null` to hide
   * it. Default is `navSearch`. The inline loading spinner won't display if the
   * left icon is hidden. */
  iconLeft?: IconName | null;

  /**
   * @deprecated `Typeahead` no longer supports a custom icon on the right side
   * of the input element. A revert icon button will still render when the input
   * value is not empty.
   */
  iconRight?: IconName | null;

  /** Displays an inline loading spinner in the place of the left icon if set to
   * true. */
  isBusy?: boolean;

  /** If no `label` is provided, `aria-label` or `aria-labelledby` must be
   * provided. */
  label?: string;

  /** Width of the menu. By default, matches width of the combobox. Note that
   * the minimum width of the dropdown is always equal to the combobox's width.
   * */
  menuWidth?: string;

  /** The height of the menu. */
  menuHeight?: string;

  /** Placeholder text for the typeahead input */
  placeholder?: string;

  /**
   * The size of the input.
   *
   * @default 'large'
   */
  size?: 'compact' | 'medium' | 'large';
};

/**
 * Combines a text input with a listbox, allowing users to filter a list of
 * options to items matching a query. Built using React Aria's
 * [ComboBox](https://react-spectrum.adobe.com/react-aria/ComboBox.html)
 * component.
 */
export const TypeaheadRoot = forwardRef<HTMLInputElement, TypeaheadRootProps>(
  (
    {
      children,
      className,
      classNamePopOver,
      defaultItems,
      defaultSelectedKey,
      defaultInputValue,
      emptyStateMessage,
      errorMessage,
      helpText,
      iconLeft = 'navSearch',
      inputValue,
      isBusy = false,
      isDisabled = false,
      isInvalid = false,
      items,
      label,
      menuHeight,
      menuWidth: menuWidthFromProps,
      menuTrigger,
      onSelectionChange,
      placeholder,
      selectedKey,
      size = 'large',
      ...props
    },
    ref
  ) => {
    // Calculates the width of the trigger element so that the popover can be
    // the same width (unless a `menuWidth` prop is provided). RAC provides a
    // `--trigger-width` CSS custom property, but it's based on the Input
    // component. This is the the same approach that RAC takes, and the approach
    // is recommended here:
    //
    // https://github.com/adobe/react-spectrum/discussions/8509.
    const triggerRef = useRef<HTMLDivElement>(null);
    const [triggerWidth, setTriggerWidth] = useState<string | null>(null);
    const onResize = useCallback(() => {
      if (triggerRef.current) {
        const inputRect = triggerRef.current.getBoundingClientRect();
        const minX = inputRect.left;
        const maxX = inputRect.right;
        setTriggerWidth(`${maxX - minX}px`);
      }
    }, [triggerRef, setTriggerWidth]);
    // I'm not sure why we get 'Unsafe call of an `any` typed value.'
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    useResizeObserver({
      ref: triggerRef,
      onResize: onResize,
    });

    // Gives preference to the `menuWidth` prop. Otherwise, falls back to the
    // trigger width calculated above.
    const getMenuWidth = () => {
      if (menuWidthFromProps) {
        return menuWidthFromProps;
      }
      return triggerWidth ?? undefined;
    };

    // Explicitly sets the same default filter used by react-aria-components
    // ComboBox because passing `defaultFilter={undefined}` to `ComboBox` breaks
    // all filtering.
    //
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { contains } = useFilter({ sensitivity: 'base' });
    const defaultFilter = props.defaultFilter ?? contains;

    const isEmptyItems =
      typeof children === 'function' &&
      Array.from(items ?? defaultItems ?? []).length === 0;
    const isEmptyNode = !children;
    const isEmpty = isEmptyItems || isEmptyNode;

    const getLeftIconSize = () => {
      if (size === 'compact') return '16px';
      if (size === 'large') return '24px';

      // Default to medium size icon.
      return '20px';
    };

    const typeaheadContextValue = useMemo(
      () => ({
        size: size,
      }),
      [size]
    );

    return (
      <Provider values={[[TypeaheadContext, typeaheadContextValue]]}>
        <ComboBox
          {...props}
          className={(renderProps) =>
            mergeClassNames([styles.comboBox, className], renderProps)
          }
          defaultFilter={defaultFilter}
          defaultItems={defaultItems}
          defaultSelectedKey={defaultSelectedKey}
          defaultInputValue={defaultInputValue}
          inputValue={inputValue}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          items={items}
          menuTrigger={menuTrigger}
          onSelectionChange={onSelectionChange}
          selectedKey={selectedKey}
          allowsEmptyCollection
          data-zds
          shouldFocusWrap
        >
          {label && (
            <Label
              isCompact={size === 'compact'}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
            >
              {isInvalid && (
                <Icon
                  color="StatusErrorStronger"
                  isBlock
                  name="formXCircle"
                  size={18}
                />
              )}
              {label}
            </Label>
          )}
          <div
            className={clsx(
              styles.inputWrapper,
              !iconLeft && styles.inputWrapperNoLeftIcon
            )}
            data-disabled={isDisabled ? 'true' : undefined}
            data-invalid={isInvalid ? 'true' : undefined}
            data-size={size}
            data-zds
            ref={triggerRef}
          >
            {iconLeft && (
              <div className={styles.iconLeftWrapper} data-size={size} data-zds>
                {isBusy ? (
                  <Spinner />
                ) : (
                  <Icon name={iconLeft} size={getLeftIconSize()} />
                )}
              </div>
            )}
            <Input
              data-content={isEmpty ? 'no-content' : 'has-content'}
              className={styles.input}
              data-zds
              data-size={size}
              placeholder={placeholder}
              ref={ref}
            />
            <TypeaheadRightSlot isTypeaheadDisabled={isDisabled} size={size} />
          </div>
          {isInvalid && errorMessage ? (
            <ErrorMessage isCompact={size === 'compact'}>
              {errorMessage}
            </ErrorMessage>
          ) : (
            helpText && (
              <Description isCompact={size === 'compact'}>
                {helpText}
              </Description>
            )
          )}
          <Popover
            data-content={isEmpty ? 'no-content' : 'has-content'}
            className={clsx(styles.popover, classNamePopOver)}
            offset={5}
            style={{ '--menu-width': getMenuWidth() }}
            data-zds
            triggerRef={triggerRef}
          >
            <ListBox
              className={clsx(
                styles.listBox,
                menuHeight && styles.customHeight
              )}
              renderEmptyState={() => (
                <div
                  className={clsx(styles.listBox, styles.emptyState)}
                  data-zds
                >
                  {emptyStateMessage ??
                    'No matches found. Try a different word.'}
                </div>
              )}
              style={{
                '--menu-height': menuHeight,
              }}
              data-zds
            >
              {children}
            </ListBox>
          </Popover>
        </ComboBox>
      </Provider>
    );
  }
);

export const Typeahead = Object.assign(TypeaheadRoot, {
  Item: TypeaheadItem,
  Section: TypeaheadSection,
});
